import Queries from './quick-queries';
import { makeUrl } from './makeUrl';
// eslint-disable-next-line no-restricted-imports
import { isPossiblyValidOrgName } from '@trello/organizations/src/isPossiblyValidOrgName';
import { Routes } from './Routes';
import { getRecordedFeatureFlag } from './getRecordedFeatureFlag';

const shouldUseNewQueries = () =>
  getRecordedFeatureFlag(
    'fep.use-new-quick-queries',
    'true', // using strings to preserve the 'true' value in stored snapshot
  ) === 'true';

export const preloadUrlHash: Record<
  string,
  {
    getUrls: (pathname?: string) => [url: string, operationName: string][];
  }
> = {
  // we've come directly to trello.com, so load the data for the member boards page
  [Routes.MemberHome]: {
    getUrls: () => {
      return [
        [
          makeUrl('/1/Members/me', Queries.memberBoards),
          'quick-queries:memberBoards',
        ],
      ];
    },
  },

  // we've come to /:username/boards
  // eslint-disable-next-line @trello/no-module-logic
  ...(shouldUseNewQueries()
    ? {
        [Routes.MemberAllBoards]: {
          getUrls: () => {
            return [
              [
                makeUrl('/1/Members/me', Queries.memberAllBoards),
                'quick-queries:memberAllBoards',
              ],
            ];
          },
        },
      }
    : {}),
  // we're performing a search using '//', but there was no search term
  [Routes.EmptySearch]: {
    getUrls: () => {
      return [
        [
          makeUrl('/1/Members/me', Queries.memberQuickBoards),
          'quick-queries:memberQuickBoards',
        ],
      ];
    },
  },
  // we're performing a search using '//'
  [Routes.Search]: {
    getUrls: (pathname = window.location.pathname) => {
      const searchTerm = new RegExp(Routes.Search).exec(pathname);
      const search = decodeURIComponent(searchTerm?.[1] || '')
        .toLowerCase()
        .replace(/[-_ ]+/g, ' ');
      return [
        [
          makeUrl('/1/Members/me', Queries.memberQuickBoards),
          'quick-queries:memberQuickBoards',
        ],
        [
          makeUrl('/1/search', Queries.quickBoardsSearch(search)),
          'quick-queries:quickBoardsSearch',
        ],
      ];
    },
  },
  // we're on a board route
  [Routes.Board]: {
    getUrls: (pathname = window.location.pathname) => {
      const idBoard = new RegExp(Routes.Board).exec(pathname)?.[1];
      return [
        [
          makeUrl(`/1/Boards/${idBoard}`, Queries.currentBoardFull),
          'quick-queries:currentBoardFull',
        ],
      ];
    },
  },
  // we're on the old school board route
  [Routes.BoardOld]: {
    getUrls: (pathname = window.location.pathname) => {
      const idBoard = new RegExp(Routes.BoardOld).exec(pathname)?.[1];
      return [
        [
          makeUrl(`/1/Boards/${idBoard}`, Queries.currentBoardFull),
          'quick-queries:currentBoardFull',
        ],
      ];
    },
  },
  // we're on the card route
  [Routes.Card]: {
    getUrls: (pathname = window.location.pathname) => {
      const idCard = new RegExp(Routes.Card).exec(pathname)?.[1];
      return [
        [makeUrl(`/1/Cards/${idCard}`, Queries.card), 'quick-queries:card'],
      ];
    },
  },
  // we might be on an organization
  [Routes.Organization]: {
    getUrls: (pathname = window.location.pathname) => {
      const orgName = new RegExp(Routes.Organization).exec(pathname)?.[1];
      return isPossiblyValidOrgName(orgName || '')
        ? [
            [
              makeUrl(
                `/1/Organizations/${orgName}`,
                Queries.workspaceBoardsPageMinimal,
              ),
              'quick-queries:workspaceBoardsPageMinimal',
            ],
          ]
        : [];
    },
  },
};
